import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { TrendingUp, TrendingDown, Activity } from 'lucide-react';
import { fetchFundingRates } from '../lib/coinglass';
import { FundingRateSimple } from '../types/coinglass';

interface FundingRateAlertsProps {
  symbol?: string;
  thresholds?: {
    high: number;
    medium: number;
    low: number;
  };
}

const FundingRateAlerts: React.FC<FundingRateAlertsProps> = ({
  symbol = 'BTC',
  thresholds = { high: 0.01, medium: 0.005, low: 0.001 }
}) => {
  const [alertsEnabled, setAlertsEnabled] = useState(false);

  const { data, isLoading } = useQuery<FundingRateSimple[]>({
    queryKey: ['fundingRates', symbol],
    queryFn: () => fetchFundingRates(symbol),
    refetchInterval: 60000, // 1 minute for alerts
  });

  if (isLoading || !data) return null;

  if (!data) return null;

  const alerts = data.filter(item => {
    const absRate = Math.abs(item.fundingRate);
    return absRate >= thresholds.low;
  }).map(item => {
    const absRate = Math.abs(item.fundingRate);
    let severity: 'high' | 'medium' | 'low' = 'low';
    
    if (absRate >= thresholds.high) severity = 'high';
    else if (absRate >= thresholds.medium) severity = 'medium';

    return {
      ...item,
      severity,
      absRate
    };
  }).sort((a, b) => b.absRate - a.absRate);

  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case 'high': return 'border-red-500 bg-red-500/10 text-red-400';
      case 'medium': return 'border-yellow-500 bg-yellow-500/10 text-yellow-400';
      default: return 'border-blue-500 bg-blue-500/10 text-blue-400';
    }
  };

  const getSeverityIcon = (severity: string) => {
    switch (severity) {
      case 'high': return <Activity className="w-4 h-4" />;
      case 'medium': return <TrendingUp className="w-4 h-4" />;
      default: return <TrendingDown className="w-4 h-4" />;
    }
  };

  return (
    <div className="bg-zinc-900 rounded-xl p-6 border border-zinc-800">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-white">Funding Rate Alerts</h3>
        <div className="flex items-center gap-2">
          <span className="text-sm text-gray-400">Alerts</span>
          <button
            onClick={() => setAlertsEnabled(!alertsEnabled)}
            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
              alertsEnabled ? 'bg-yellow-400' : 'bg-zinc-700'
            }`}
          >
            <span
              className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                alertsEnabled ? 'translate-x-6' : 'translate-x-1'
              }`}
            />
          </button>
        </div>
      </div>

      {alerts.length === 0 ? (
        <div className="text-center py-4">
          <p className="text-gray-400">No significant funding rate movements</p>
        </div>
      ) : (
        <div className="space-y-3 max-h-60 overflow-y-auto">
          {alerts.slice(0, 5).map((alert) => (
            <div
              key={alert.exchangeName}
              className={`p-3 rounded-lg border ${getSeverityColor(alert.severity)}`}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  {getSeverityIcon(alert.severity)}
                  <div>
                    <div className="font-medium">{alert.exchangeName}</div>
                    <div className="text-sm opacity-75">
                      Funding rate: {(alert.fundingRate * 100).toFixed(4)}%
                    </div>
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-xs uppercase font-medium">{alert.severity}</div>
                  <div className="text-xs opacity-75">
                    {alert.fundingRate > 0 ? 'Longs pay' : 'Shorts pay'}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="mt-4 pt-4 border-t border-zinc-800">
        <div className="flex justify-between text-xs text-gray-400">
          <span>High: ≥{(thresholds.high * 100).toFixed(2)}%</span>
          <span>Medium: ≥{(thresholds.medium * 100).toFixed(2)}%</span>
          <span>Low: ≥{(thresholds.low * 100).toFixed(2)}%</span>
        </div>
      </div>
    </div>
  );
};

export default FundingRateAlerts;
